<script setup>
import VideoGen from './components/VideoGen.vue'
import ImageGen from './components/ImageGen.vue'
import FaceSwap from './components/FaceSwap.vue'
import LoraGen from './components/LoraGen.vue'
import SceneEdit from './components/SceneEdit.vue'
import { ref } from 'vue'

const isDark = ref(false)
const currentStep = ref(0)

const steps = [
  { title: '幻想创造', component: ImageGen, key: 'ImageGen' },
  { title: '场景补充', component: SceneEdit, key: 'SceneEdit' },
  { title: '容颜焕新', component: FaceSwap, key: 'FaceSwap' },
  { title: '梦境成真', component: VideoGen, key: 'VideoGen' },
  { title: '私密定制', component: LoraGen, key: 'LoraGen' },
]

function toggleTheme() {
  isDark.value = !isDark.value
  document.documentElement.classList.toggle('dark-theme')
}

function nextStep() {
  if (currentStep.value < steps.length - 1) {
    currentStep.value++
  }
}

function prevStep() {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}
</script>

<template>
  <div class="app">
    <header class="header">
      <div class="theme-toggle" @click="toggleTheme">
        {{ isDark ? '🌞' : '🌙' }}
      </div>
      <h1>AI Live photo</h1>
      <p class="subtitle">基于 AI 的虚拟个人秀</p>
    </header>
    <div class="content">
      <div class="steps-nav">
        <div 
          v-for="(step, index) in steps" 
          :key="index"
          class="step"
          :class="{ 
            'active': index === currentStep,
            'completed': index < currentStep 
          }"
          @click="currentStep = index"
        >
          <div class="step-number">{{ index + 1 }}</div>
          <div class="step-title">{{ step.title }}</div>
          <div class="step-line" v-if="index < steps.length - 1"></div>
        </div>
      </div>
      <div class="step-content">
        <keep-alive>
          <component 
            :is="steps[currentStep].component"
          ></component>
        </keep-alive>
      </div>
      <div class="step-buttons">
        <button 
          @click="prevStep" 
          :disabled="currentStep === 0"
          class="step-button"
        >
          上一步
        </button>
        <button 
          @click="nextStep" 
          :disabled="currentStep === steps.length - 1"
          class="step-button"
        >
          下一步
        </button>
      </div>
    </div>
    <footer class="footer">
      <p>© 2024 AI Live photo. Powered by IDEA</p>
    </footer>
  </div>
</template>

<style>
:root {
  --bg-color: linear-gradient(135deg, #ff758c 0%, #ff7eb3 100%);
  --text-color: #213547;
  --header-color: #2c3e50;
  --subtitle-color: #666;
  --card-bg: rgba(255, 255, 255, 0.75);
  --card-shadow: rgba(0, 0, 0, 0.2);
  --section-bg: rgba(255, 255, 255, 0.7);
  --border-color: rgba(102,126,234,0.2);
  --primary-color: #ff477e;
  --focus-shadow: rgba(102,126,234,0.2);
  --input-bg: rgba(255, 255, 255, 0.9);
  --log-bg: #1e1e1e;
  --log-text: #00ff00;
  --gradient-start: #ff477e;
  --gradient-end: #ff7eb3;
}

:root.dark-theme {
  --bg-color: linear-gradient(135deg, #2b1b2c 0%, #1a0f2e 100%);
  --text-color: #e1e1e1;
  --header-color: #ffffff;
  --subtitle-color: #a0a0a0;
  --card-bg: rgba(40, 44, 52, 0.95);
  --card-shadow: rgba(0, 0, 0, 0.4);
  --section-bg: rgba(30, 34, 42, 0.8);
  --border-color: rgba(102,126,234,0.3);
  --primary-color: #ff69b4;
  --focus-shadow: rgba(102,126,234,0.3);
  --input-bg: rgba(30, 34, 42, 0.9);
  --log-bg: #2d3748;
  --log-text: #4ade80;
  --gradient-start: #ff69b4;
  --gradient-end: #da70d6;
}

body {
  margin: 0;
  min-height: 100vh;
  color: var(--text-color);
  background: var(--bg-color);
  transition: color 0.5s, background-color 0.5s;
}

.app {
  padding: 20px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.header {
  text-align: center;
  margin-bottom: 40px;
}

.header h1 {
  font-size: 2.5em;
  color: var(--header-color);
  margin-bottom: 10px;
  text-shadow: 2px 2px 15px rgba(255, 71, 126, 0.3);
  animation: floating 3s ease-in-out infinite;
}

.subtitle {
  color: var(--subtitle-color);
  font-size: 1.1em;
  margin: 0;
  text-shadow: 1px 1px 10px rgba(255, 71, 126, 0.2);
}

.footer {
  text-align: center;
  margin-top: auto;
  padding: 20px;
  color: #666;
  font-size: 0.9em;
}

/* Markdown 全局样式 */
.markdown-body h1,
.markdown-body h2,
.markdown-body h3 {
    margin-top: 24px;
    margin-bottom: 16px;
    font-weight: 600;
    line-height: 1.25;
    color: #213547;
}

.markdown-body code {
    padding: 0.2em 0.4em;
    margin: 0;
    font-size: 85%;
    background-color: #f6f8fa;
    border-radius: 3px;
}

.markdown-body pre {
    padding: 16px;
    overflow: auto;
    font-size: 85%;
    line-height: 1.45;
    background-color: #f6f8fa;
    border-radius: 3px;
    border: 1px solid #eee;
}

/* 添加链接样式 */
.markdown-body a {
    color: #0366d6;
    text-decoration: none;
}

.markdown-body a:hover {
    text-decoration: underline;
}

/* 添加列表样式 */
.markdown-body ul,
.markdown-body ol {
    padding-left: 2em;
}

.markdown-body li {
    margin: 0.25em 0;
}

.divider {
  margin: 40px 0;
  border-top: 1px solid rgba(0,0,0,0.1);
}

.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 40px;
  max-width: 1800px;
  margin: 0 auto;
  width: 100%;
  padding: 0 20px;
  align-items: center;
}

.content-wrapper {
  display: flex;
  gap: 20px;
  width: 100%;
}

.left-panel, .right-panel {
  flex: 1;
  min-width: 0;
}

.theme-toggle {
  position: absolute;
  top: 20px;
  right: 20px;
  font-size: 24px;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--card-bg);
  border-radius: 50%;
  box-shadow: 0 2px 8px var(--card-shadow);
  transition: all 0.3s ease;
}

.theme-toggle:hover {
  transform: scale(1.1);
}

.steps-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  position: relative;
  padding: 0 40px;
  max-width: 1200px;
  width: 100%;
}

.step {
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  flex: 1;
  transition: all 0.5s ease;
  padding: 10px;
  border-radius: 20px;
}

.step:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--card-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: var(--text-color);
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
  animation: subtle-pulse 2s infinite;
}

.step.active .step-number {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.step.completed .step-number {
  background: var(--gradient-start);
  color: white;
  border-color: var(--gradient-start);
}

.step-title {
  margin-left: 12px;
  color: var(--text-color);
  font-weight: 500;
}

.step-line {
  flex: 1;
  height: 2px;
  background: var(--border-color);
  margin: 0 20px;
}

.step.completed .step-line {
  background: var(--gradient-start);
}

.step-content {
  margin: 0 auto;
  max-width: 1200px;
  width: 100%;
}

.step-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
  padding: 0 40px;
}

.step-button {
  padding: 12px 24px;
  background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
}

.step-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px var(--focus-shadow);
}

.step-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@keyframes subtle-pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
}

@keyframes floating {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
}
</style> 